import { CheckCircle, MagnifyingGlass } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { formatAsMoney } from "../../../../helpers/numbers";
import BillModal from "./BillModal";

/** Normalized plan shape the modal renders, mapped from data / cable plans. */
export type BillPlanOption = {
  id: string;
  name: string;
  subtitle?: string | null;
  badge?: string;
  amount: number;
  validity?: string | null;
};

interface PlanSelectModalProps {
  open: boolean;
  onClose: () => void;
  plans: BillPlanOption[];
  selectedId?: string;
  onSelect: (id: string) => void;
  loading?: boolean;
  /** Cable bouquets aren't grouped by validity, so tabs can be hidden. */
  showTabs?: boolean;
}

const TAB_CATEGORIES = [
  { key: "all", label: "All" },
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "long", label: "3 Months+" },
] as const;

type TabKey = (typeof TAB_CATEGORIES)[number]["key"];

const categorizePlan = (plan: BillPlanOption): TabKey => {
  const v = (plan.validity ?? "").toLowerCase();
  if (/\b(1\s*day|daily|24\s*hour)\b/.test(v)) return "daily";
  if (/\b(week|7\s*day|14\s*day)\b/.test(v)) return "weekly";
  if (/\b(1\s*month|month|30\s*day|mtly)\b/.test(v) && !/\b[2-9]\s*month/.test(v))
    return "monthly";
  if (/\b([3-9]\s*month|year|annual|quart|6\s*month|12\s*month|season)\b/.test(v))
    return "long";
  const dayMatch = v.match(/^(\d+)\s*day/);
  if (dayMatch) {
    const days = parseInt(dayMatch[1], 10);
    if (days <= 1) return "daily";
    if (days <= 14) return "weekly";
    if (days <= 45) return "monthly";
    return "long";
  }
  return "all";
};

const PlanSelectModal = ({
  open,
  onClose,
  plans,
  selectedId,
  onSelect,
  loading,
  showTabs = true,
}: PlanSelectModalProps) => {
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<TabKey>("all");

  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = { all: plans.length };
    for (const plan of plans) {
      const cat = categorizePlan(plan);
      counts[cat] = (counts[cat] ?? 0) + 1;
    }
    return counts;
  }, [plans]);

  const filteredPlans = useMemo(() => {
    let result = plans;

    if (showTabs && activeTab !== "all") {
      result = result.filter((p) => categorizePlan(p) === activeTab);
    }

    const query = search.trim().toLowerCase();
    if (query) {
      result = result.filter((plan) =>
        [plan.name, plan.subtitle, plan.validity, plan.amount]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query)
      );
    }

    return result;
  }, [plans, activeTab, search, showTabs]);

  const handleClose = () => {
    setSearch("");
    setActiveTab("all");
    onClose();
  };

  const visibleTabs = TAB_CATEGORIES.filter(
    (tab) => tab.key === "all" || (tabCounts[tab.key] ?? 0) > 0
  );

  return (
    <BillModal
      open={open}
      onClose={handleClose}
      title="Select a plan"
      description="Search by name or price, or browse by category."
    >
      <div className="flex flex-col gap-3">
        {/* Search */}
        <div className="flex items-center gap-2 rounded-xl border border-gray-300 shadow-sm px-4 py-2.5 focus-within:outline focus-within:outline-deepBlue">
          <MagnifyingGlass className="text-gray-400 shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search plans..."
            className="w-full outline-none text-sm"
          />
        </div>

        {/* Tabs */}
        {showTabs && visibleTabs.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition ${
                  activeTab === tab.key
                    ? "bg-deepBlue text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Plan list */}
        {loading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100" />
            ))}
          </div>
        ) : filteredPlans.length > 0 ? (
          <div className="flex flex-col gap-2">
            {filteredPlans.map((plan) => {
              const active = selectedId === plan.id;
              return (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => {
                    onSelect(plan.id);
                    handleClose();
                  }}
                  className={`flex items-center justify-between gap-3 rounded-xl border p-3.5 text-left transition ${
                    active
                      ? "border-visaro-orange bg-visaro-orange/5"
                      : "border-gray-200 hover:border-visaro-orange/60"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 truncate">
                        {plan.name}
                      </span>
                      {plan.badge && (
                        <span className="shrink-0 rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                          {plan.badge}
                        </span>
                      )}
                    </div>
                    {plan.subtitle && (
                      <div className="text-xs text-gray-500 truncate">
                        {plan.subtitle}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-bold text-deepBlue text-sm">
                      NGN {formatAsMoney(plan.amount)}
                    </span>
                    {active && (
                      <CheckCircle
                        size={20}
                        weight="fill"
                        className="text-visaro-orange"
                      />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="rounded-xl bg-gray-50 border border-dashed border-gray-300 p-8 text-center text-gray-500 text-sm">
            No plans found. Try a different search or category.
          </div>
        )}
      </div>
    </BillModal>
  );
};

export default PlanSelectModal;
